import * as THREE from 'three'
import Experience from '../Experience'
import { createNoise3D } from 'simplex-noise'
import Input from '../Input'

// Create a noise function with a custom random function
const noise3D = createNoise3D(Math.random);

export default class Brain
{
    constructor()
    {
        this.experience = new Experience()
        
        this.scene = this.experience.scene
        this.resources = this.experience.resources
        this.camera = this.experience.camera
        this.time = this.experience.time
        this.input = this.experience.input        

        this.debug = this.experience.debug

        this.brainGroup = new THREE.Group()

        if(this.debug.active)
        {
            this.debugFolder = this.debug.ui.addFolder('Brain')
            this.debugFolderLighting = this.debug.ui.addFolder('Lighting')
        }
        
        
        this.createBrainMaterialWithShader()
        this.createBrainCoreMaterial()
        this.setModel()
        this.addLights()
        console.log('brain initialized')
        //this.animate()
    }

    addLights()
    {
        // Add point light
        this.pointLight = new THREE.PointLight(0xf7931a, 500, 500, 2.5)
        this.pointLight2 = new THREE.PointLight(0xf7931a, 500, 500, 2.5)
        
        this.pointLight.position.set(0, -6.7, 0)
        this.pointLight.castShadow = false
        this.scene.add(this.pointLight)

        this.pointLight2.position.set(0, 6.7, 0)
        this.pointLight2.castShadow = false
        // this.scene.add(this.pointLight2)

        if(this.debug.active)
        {
            // POINT LIGHT 1
            this.debugFolderLighting
                .addColor(this.pointLight, 'color')
                .name('point light color')
                .onFinishChange(() => {
                    console.log(this.pointLight.color.getHexString())
                })
        
            this.debugFolderLighting
                .add(this.pointLight, 'intensity')
                .name('point light intensity')
                .min(0)
                .max(1000)
                .step(1)
                .onFinishChange(() => {
                    console.log(this.pointLight.intensity)
                })

            this.debugFolderLighting
                .add(this.pointLight, 'distance')
                .name('point light distance')
                .min(0)
                .max(1000)
                .step(1)
                .onFinishChange(() => {
                    console.log(this.pointLight.distance)
                })

            this.debugFolderLighting
                .add(this.pointLight, 'decay')
                .name('point light decay')
                .min(0)
                .max(10)
                .step(0.1)
            
            this.debugFolderLighting
                .add(this.pointLight.position, 'y')
                .name('point light Y')
                .min(-10)
                .max(10)
                .step(0.1)

            // POINT LIGHT 2
            this.debugFolderLighting
                .addColor(this.pointLight2, 'color')
                .name('point light 2 color')
                .onFinishChange(() => {
                    console.log(this.pointLight2.color.getHexString())
                })
    
            this.debugFolderLighting    
                .add(this.pointLight2, 'intensity')
                .name('point light 2 intensity')
                .min(0)
                .max(1000)
                .step(1)
            .onFinishChange(() => {
                console.log(this.pointLight2.intensity)
            })

            this.debugFolderLighting
                .add(this.pointLight2, 'distance')
                .name('point light 2 distance')
                .min(0)
                .max(1000)
                .step(1)
            .onFinishChange(() => {
                console.log(this.pointLight2.distance)
            })

            this.debugFolderLighting
                .add(this.pointLight2, 'decay')
                .name('point light 2 decay')
                .min(0)
                .max(10)
                .step(0.1)
        
            this.debugFolderLighting
                .add(this.pointLight2.position, 'y')
                .name('point light 2 Y')
                .min(-10)
                .max(10)
                .step(0.1)
                
        }
    }

    createBrainMaterialWithShader()
    {
        this.brainMaterial = new THREE.ShaderMaterial({
            uniforms: {
                time: { value: 0.0 },
                amplitude: { value: 0.2 },
                speed: { value: 0.1 },
                color: { value: new THREE.Color(0x9a9a9a) },
                emissive: { value: new THREE.Color(0xd57032) },
                emissiveIntensity: { value: 0.1 },
                alpha: { value: 0.04 }
            },
            vertexShader: `
                uniform float time;
                uniform float amplitude;
                uniform float speed;
                varying vec3 vNormal;
                void main() {
                    vNormal = normal;
                    vec3 pos = position;
                    float noise = sin(pos.x * 10.0 + time * speed) * amplitude;
                    pos += normal * noise;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
                }
            `,
            fragmentShader: `
                uniform vec3 color;
                uniform vec3 emissive;
                uniform float emissiveIntensity;
                uniform float alpha;
                varying vec3 vNormal;
                void main() {
                    vec3 finalColor = color + emissive * emissiveIntensity;
                    gl_FragColor = vec4(finalColor, alpha);
                }
            `,
            transparent: false,
            blending: THREE.AdditiveBlending,
            depthWrite: false
        });
    
        // Debug
        if(this.debug.active)
        {
            this.debugFolder
                .addColor(this.brainMaterial.uniforms.color, 'value')
                .name('brain color')
                .onFinishChange(() => {
                    console.log(this.brainMaterial.uniforms.color.value.getHexString())
                });
    
            this.debugFolder
                .addColor(this.brainMaterial.uniforms.emissive, 'value') 
                .name('emissive color')
                .onFinishChange(() => {
                    console.log(this.brainMaterial.uniforms.emissive.value.getHexString())
                });
    
            this.debugFolder
                .add(this.brainMaterial.uniforms.emissiveIntensity, 'value')
                .min(0)
                .max(1)
                .step(0.01)
                .name('emissive intensity');

            this.debugFolder
                .add(this.brainMaterial.uniforms.alpha, 'value')
                .min(0)
                .max(1)
                .step(0.001)
                .name('alpha')
                .onFinishChange(() => {
                    console.log(this.brainMaterial.uniforms.alpha.value)
                })
                
        }
    }

    createBrainCoreMaterial()
    {
        this.brainCoreMaterial = new THREE.MeshStandardMaterial({
            color: 0xf6b635,
            emissive: 0xd57032,
            emissiveIntensity: .1,
            transparent: true,
            opacity: 0.6
        })

        // DEBUG SOLID MAT
        // this.brainCoreMaterial = new THREE.MeshStandardMaterial({
        //     color: 0xf6b635,
        //     emissive: 0xd57032,
        //     emissiveIntensity: .1,
        //     transparent: false,
            
        // })

        

        // Debug
        if(this.debug.active)
        {
            this.debugFolder
                .addColor(this.brainCoreMaterial, 'color')
                .name('brain color')
                .onFinishChange(() => {
                    console.log(this.brainCoreMaterial.color.getHexString())
                })

            this.debugFolder
                .addColor(this.brainCoreMaterial, 'emissive') 
                .name('emissive color')
                .onFinishChange(() => {
                    console.log(this.brainCoreMaterial.emissive.getHexString())
                })

            this.debugFolder
                .add(this.brainCoreMaterial, 'emissiveIntensity')
                .min(0)
                .max(1)
                .step(0.01)
                .name('emissive intensity')
                
        }
    }

    setModel()
    {
        console.log('setting models')
        this.model = this.resources.items.brainModel.clone();
        this.modelCore = this.resources.items.brainModel.clone();
        

        if(this.model) {
            this.model.traverse((child) =>
            {
                if(child instanceof THREE.Mesh)
                {
                    child.material = this.brainMaterial;
                    child.renderOrder = 1; // Set higher render order for brainMaterial
                }
            })
        }

        this.modelCore.traverse((child) =>
        {
            if(child instanceof THREE.Mesh)
            {
                child.material = this.brainCoreMaterial;
                child.renderOrder = 0; // Set lower render order for brainCoreMaterial
            }
        })


        // Brain Computational Energy
        if(this.model) {
            this.model.scale.set(3.363, 3.363, 3.363)
            //this.model.scale.set(0,0,0)
            
            // this.model.scale.set(0.01, 0.01, 0.01)
            this.model.position.set(0, -1.88, 0)
        }

        // Core of the Brain
        if(this.modelCore) {
            //this.modelCore.scale.set(2.5, 2.5, 2.5)
            
            this.modelCore.scale.set(1.01, 1.01, 1.01)
            this.modelCore.position.set(0, -1.88, 0)
        }

        if(this.debug.active)
        {
            // Add uniform scale control
            const updateScale = (value) => {
                this.model.scale.set(value, value, value)
            }

            this.debugFolder.add({ scale: 1 }, 'scale')
                .name('scale')
                .min(0.0001)
                .max(100)
                .step(0.0001)
                .onChange(updateScale)

            // Add Y position control
            const updateYPosition = (value) => {
                this.model.position.y = value
            }

            this.debugFolder.add({ yPosition: 0 }, 'yPosition')
                .name('Y position')
                .min(-5)
                .max(5)
                .step(0.01)
                .onChange(updateYPosition)
        }
        
        this.brainGroup.add(this.model)
        this.brainGroup.add(this.modelCore)
          
        this.scene.add(this.brainGroup)
    }

    update()
    {
        
        if(this.model) 
        {
            this.model.rotation.y += 0.01
            this.animateShader()
        }
        if(this.modelCore) {
            this.modelCore.rotation.y += 0.01
        }
        
        // Pulse the core scale using a sine wave with increased frequency
        const pulseScale = (50 + Math.sin(this.time.elapsed * 16 ))/16 // Increased frequency by doubling the multiplier

        if(this.modelCore) {
            this.modelCore.scale.set(pulseScale, pulseScale, pulseScale)
        }

        // Calculate distance from center (0,0) using mouseVector
        const mouseDistance = Math.sqrt(
            Math.pow(this.input.mouseVector.x, 2) + 
            Math.pow(this.input.mouseVector.y, 2)
        );

        // Map distance (0 to ~1.414) to decay range (1 to 3)
        const mappedDecay = 1 + (2 * mouseDistance / Math.sqrt(2));
        
        // Update pointLight decay
        this.pointLight.decay = mappedDecay;
        this.pointLight2.decay = mappedDecay;
    }

    animateShader()
    {
        // this number changes the speed of the shader
        //return;
        this.brainMaterial.uniforms.time.value = this.time.elapsed * 80;
    }
    
    
    
}